import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Textarea } from '@workspace/ui/components/textarea'
import { useToast } from '@workspace/ui/components/toast'
import { FileText } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { trackEvent } from '../../../../lib/gtag'
import { copy } from '../../copy/investor-copy'
import { useSavePrivateNote } from '../../hooks/use-investor-assistant'
import { useSubmitOnce } from '../../hooks/use-submit-once'
import { formatShortDate } from '../following/dates'
import { PrivateTag, RelCard } from './rel-card'
import type { Relationship } from './types'

/**
 * The investor's own note on a startup: private to them, written in the app. It shows once there is
 * a note or while one is being written; there is no delete control (an empty save removes it).
 */
export function NoteCard({
  data,
  editing,
  onEditingChange,
}: {
  data: Relationship
  editing: boolean
  onEditingChange: (editing: boolean) => void
}) {
  const note = data.note
  const [text, setText] = useState(note?.text ?? '')
  const save = useSavePrivateNote()
  const once = useSubmitOnce(save.isPending)
  const toast = useToast()
  // Writing a note: the box takes the cursor and comes into view, wherever the card sits.
  const box = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    if (!editing) return
    // After the menu has closed and given focus back, which it does a moment later.
    const timer = window.setTimeout(() => {
      box.current?.focus({ preventScroll: true })
      box.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }, 150)
    return () => window.clearTimeout(timer)
  }, [editing])
  if (!note && !editing) return null

  const startEditing = () => {
    setText(note?.text ?? '')
    onEditingChange(true)
  }

  return (
    <RelCard title={copy('note.title')} right={<PrivateTag label={copy('ai.private')} />}>
      {editing ? (
        <div className="mt-3 space-y-2">
          <Textarea
            ref={box}
            rows={5}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={copy('note.placeholder')}
            aria-label={copy('note.aria')}
            maxLength={5000}
            className="border border-border bg-[#0F0F10]"
          />
          <p className="text-xs text-muted-foreground">{copy('note.consent')}</p>
          <div className="flex items-center justify-end gap-4">
            <button
              type="button"
              onClick={() => onEditingChange(false)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              {copy('note.cancel')}
            </button>
            <CtaButton
              size="sm"
              loading={save.isPending}
              onClick={() =>
                once(() =>
                  save.mutate(
                    { path: { startupId: data.startup.id }, body: { text } },
                    {
                      onSuccess: () => {
                        trackEvent('note_saved', {
                          startup_id: data.startup.id,
                          cleared: text.trim() === '',
                        })
                        toast(copy(text.trim() === '' ? 'toast.note.cleared' : 'toast.note.saved'))
                        onEditingChange(false)
                      },
                      onError: () => toast(copy('toast.note.error')),
                    }
                  )
                )
              }
            >
              {copy('note.save')}
            </CtaButton>
          </div>
        </div>
      ) : (
        note && (
          <>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
              {note.text}
            </p>
            <div className="mt-3 flex items-center justify-between gap-3">
              <SecondaryButton size="sm" icon={<FileText />} onClick={startEditing}>
                {copy('note.edit')}
              </SecondaryButton>
              <span className="text-xs text-muted-foreground">
                {copy('note.edited', { date: formatShortDate(note.updatedAt) })}
              </span>
            </div>
          </>
        )
      )}
    </RelCard>
  )
}
